<template>
  <section :class="{ loaded: loaded, 'show-content': showContent }">
    <header ref="headerElement" class="">
      <UtilsBackgroundEffect
        class="background-solid"
        :class="{ visible: loaded }"
        :height="heightHeader"
        :mass="0.3"
        @update-height="updateHeightAnimated"
      />
      <div class="graphic">
        <img
          v-if="imgUrl"
          :style="style"
          :src="imgUrl"
          :alt="libraryBook.metaTitle"
          @load="handleLoaded"
        />
      </div>

      <div class="heading">
        <div class="text">
          <h2 class="title">{{ titleUsed }}</h2>
          <span v-if="subtitleUsed" class="subtitle">{{ subtitleUsed }}</span>
        </div>
        <div class="actions">
          <LbAddContent
            class="add-content"
            :children="library ? library.children : [libraryBook]"
            :auto-redirect="isInUserContent"
          />
        </div>
      </div>
    </header>
    <div v-cleaned="contentUsed" class="content"></div>
  </section>
</template>
<script lang="ts" setup>
import type { LibraryBookSchema } from '~/types/schema';
interface Props {
  libraryBook: LibraryBookSchema;
  title?: string;
  subtitle?: string;
  content?: string;
  mediaId?: string;
  bgColor?: string;
}

const props = defineProps({
  libraryBook: null,
  title: null,
  subtitle: null,
  content: null,
  mediaId: null,
  bgColor: null
});

const headerElement = ref(null);

const showContent = ref(false);

const profile = useProfileStore();

const { libraryManager } = inject(INJECT_LIBRARY_PAGE_OR_FORM);

const { library } = libraryManager;

const { height: heightHeader } = useElementBounding(headerElement);

const loaded = ref(false);

const { style, scale } = useScaleEffect(0, false, 4, 70, 0.22);

const {
  libraryManager: { media },
} = inject(INJECT_LIBRARY_PAGE_OR_FORM);

const hasTitleProps = computed(() => toValue(props.title) !== undefined);

const titleUsed = computed(() => {
  if (hasTitleProps.value) return props.title;
  return props.libraryBook.metaTitle;
});

const contentUsed = computed(() => {
  if (props.content) return props.content;
  return props.libraryBook.metaContent;
});

const subtitleUsed = computed(() => {
  if (hasTitleProps.value) return props.subtitle || null;
  return props.libraryBook.metaSubtitle || null;
});

const imgUrl = computed(() => {
  const id = props.mediaId || props.libraryBook.metaIcon;
  return media.value.has(id)
    ? media.value.get(id).urlCompressed || media.value.get(id).url
    : null;
});

const heightAnimated = ref<number>(0);

const updateHeightAnimated = (height) => {
  heightAnimated.value = toValue(height);
};
const handleLoaded = () => {
  loaded.value = true;
  scale.value = 1;
};

watch(
  () => heightAnimated.value,
  (value) => {
    const ratio = value / toValue(heightHeader);
    if (ratio > 0.99) {
      showContent.value = true;
    }
  }
);

const { all: allContents } = useContentsForUserType(ContentUserType.Default);

const isInUserContent = computed(() => {
  if (!props.libraryBook) return false;
  return (
    allContents.value.find((content) => content.id === props.libraryBook.id) !==
    null
  );
});
</script>
<style lang="scss" scoped>
section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gap_m);
  width: 100%;
}
.heading {
  display: flex;
  flex-direction: column;
  gap: var(--gap_m);
  transition: opacity 600ms $ease-out-quad;
  opacity: 0;
}
.graphic,
header,
footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: var(--gap_s);
}

.content {
  width: 100%;
  padding: 0;
  transform: scale(0.98) translateY(10px);
  transition: opacity 500ms $ease-in-out-quad 0ms,
    transform 300ms $ease-out-quad 0ms;
  opacity: 0;
  &:deep(p),
  &:deep(div),
  &:deep(li),
  &:deep(span) {
    --font_size: var(--text_size_m);
    --line_height: var(--text_lh_m);

    font-size: var(--font_size);
    line-height: var(--line_height);
  }
  &:deep(p),
  &:deep(div) {
    margin: 1.5rem 0;
  }
  &:deep(li) {
    margin: 1rem 3rem;
    list-style: disc;
  }
}
.show-content {
  .content {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

header {
  display: flex;
  position: relative;
  flex-direction: column;
  padding: 3.8rem;
  @include desktop {
    display: grid;
    grid-template-columns: 25% 1fr 25%;
  }

  .effect {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  .heading {
    z-index: 1;
  }
  .title {
    display: block;
    color: var(--color_neutral_50);
    font-style: normal;
    text-align: center;
    @include font('l', 800, 'secondary');

    // text-transform: uppercase;
  }
  .subtitle {
    display: block;
    color: var(--color_neutral_50);
    @include font('m', 600);

    text-align: center;
  }
  img {
    z-index: 1;
    object-fit: contain;
    max-width: 11rem;
  }
}

.loaded {
  .heading {
    opacity: 1;
  }
}
</style>
